/**
 * Текстовая ячейка (§5.2, тип «Текст»): примечание, доставка, установка,
 * гарантия, пользовательские text/textarea.
 *
 * Инпут 28 px в размер ячейки, дебаунс записи 400 ms, Alt+Enter разворачивает
 * поле в textarea 3 строки поверх сетки (§2.3 «Инпут»). Enter фиксирует и
 * переводит редактор на строку ниже, Shift+Enter — выше, Tab — вправо (§5.3).
 */
import { useEffect, useRef, useState } from 'react'
import { Input, Textarea } from '../../../../components/index.js'
import { tableTexts as t } from '../../texts.table.js'
import type { CellProps } from './CellBox.js'

const DEBOUNCE_MS = 400

export function TextCell(props: CellProps) {
  const { column, cell, editing, readOnly, seed, write, stepAfterCommit, commitAndRight, closeEditor } = props
  const published = typeof cell.value === 'string' ? cell.value : ''
  const [value, setValue] = useState(seed ?? published)
  const [expanded, setExpanded] = useState(column.field?.type === 'textarea')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(value)

  useEffect(() => {
    if (!editing) return
    setValue(seed ?? published)
    latest.current = seed ?? published
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  if (!editing || readOnly) {
    if (cell.empty) {
      return (
        <span className="cell__empty">
          <span className="cell__empty-dash" aria-hidden="true">{t.empty.dash}</span>
          <span className="cell__empty-label">
            <span className="cell__empty-glyph" aria-hidden="true">⌁</span>
            {column.code === 'note' ? t.empty.note : t.empty.value}
          </span>
        </span>
      )
    }
    return <span className="cell__value">{published}</span>
  }

  const flush = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    if (latest.current !== published) write(latest.current === '' ? null : latest.current)
  }

  const change = (next: string) => {
    setValue(next)
    latest.current = next
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      if (latest.current !== published) write(latest.current === '' ? null : latest.current)
    }, DEBOUNCE_MS)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (timer.current) { clearTimeout(timer.current); timer.current = null }
      closeEditor()
      return
    }
    if (event.key === 'Enter' && event.altKey) {
      event.preventDefault()
      setExpanded(true)
      return
    }
    if (event.key === 'Enter' && !expanded) {
      event.preventDefault()
      event.stopPropagation()
      flush()
      stepAfterCommit(event.shiftKey ? -1 : 1)
      return
    }
    if (event.key === 'Enter' && expanded && !event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      flush()
      stepAfterCommit(1)
      return
    }
    if (event.key === 'Tab') {
      event.preventDefault()
      event.stopPropagation()
      flush()
      commitAndRight()
    }
  }

  return (
    <div className="cell__editor" onKeyDown={onKeyDown}>
      {expanded ? (
        <Textarea
          autoFocus
          overlay
          rows={3}
          value={value}
          aria-label={column.title}
          onChange={(event) => change(event.target.value)}
          onBlur={flush}
        />
      ) : (
        <Input
          autoFocus
          size="cell"
          value={value}
          aria-label={column.title}
          onChange={(event) => change(event.target.value)}
          onBlur={flush}
        />
      )}
    </div>
  )
}
